import React, { useContext, useEffect } from 'react';

import {
  getAgent,
  getAssistedByAgent,
  getCustomerId,
  getIIASPaymentDetails,
  getMetadata,
  getPartialAuthorization,
} from '../../../../utils/session/selectors';
import useFindBankNameWithRoutingNumber from '../../../add-payment-method/components/AddPaymentMethodAchContainer/useFindBankNameWithRoutingNumber';
import { AppContext } from '../../../contextStore/AppContext';
import PaymentApi from '../../../../services/payment/PaymentApi';
import type {
  PayAndStoreAchContainerProps,
  PayAndStoreAchFormData,
} from '../../types';
import PayAndStoreAchForm from '../PayAndStoreAchForm';
import { createConsentObject } from '../../../checkout-v2/utils/createPaymentConsentObject';
import useSessionExpirationReminder from '../../../../hooks/useSessionExpirationReminder';
import { NotificationContext } from '../../../contextStore/NotificationContext';
import noop from '../../../../utils/function/noop';
import { shouldShowSaveOptions } from '../../utils/showPaymentMethodSaveSectionUtil';

const PayAndStoreAchContainer = ({
  amount,
  formTitle,
  showSaveFeatureBlock,
  isInFocus,
  onCancel,
  onSuccess,
  onBackClick,
  onBeforeSubmit,
  onLoadComplete = noop,
  paymentMethodSaveMode,
}: PayAndStoreAchContainerProps) => {
  const {
    paymentDescription,
    merchantTransactionId,
    authorizeCard,
    statementDescriptorSuffix,
    originalCheckoutSessionResponse,
    setData,
  } = useContext(AppContext);
  const { notify } = useContext(NotificationContext);

  useSessionExpirationReminder({
    notify,

    originalCheckoutSessionResponse,
  });

  const [bankName, onRoutingNumberInput] =
    useFindBankNameWithRoutingNumber();

  const agent = getAgent(originalCheckoutSessionResponse);
  const assistedByAgent = getAssistedByAgent(
    originalCheckoutSessionResponse,
  );

  const onSubmitAchForm = (data: PayAndStoreAchFormData) => {
    const consentResponse = createConsentObject(
      originalCheckoutSessionResponse,
    );
    PaymentApi.payAndStore({
      paymentMethod: {
        type: 'BANK_ACCOUNT',
        vendor: 'STRIPE',
        nickname: data.nickname,
        default: data.default,
        savePaymentMethod: data.savePaymentMethod,
        paymentMethodDetails: {
          type: 'BANK_ACCOUNT',
          accountNumber: data.accountNumber,
          routingNumber: data.routingNumber,
          accountType: data.accountType,
          nameOnAccount: data.nameOnAccount,
        },
        sourceProvider: {
          name: 'CCG',
        },
      },
      paymentDescription,
      paymentDetails: getIIASPaymentDetails(
        originalCheckoutSessionResponse,
      ),
      amount,
      merchantTransactionId,
      authorizeCard,
      partialAuthorization: getPartialAuthorization(
        originalCheckoutSessionResponse,
      ),
      statementDescriptorSuffix,
      metadata: getMetadata(originalCheckoutSessionResponse),
      consent: consentResponse,
      agent,
      assistedByAgent,
    })
      .then(() => {
        onSuccess?.();
      })
      .catch((err: unknown) => {
        // We don't call onError because that calls notify which alerts the user with a non user friendly message.
        // The error from the backend is shown to the user via axios interceptor.
        console.error(err);
      })
      .finally(() => {
        setData({ overlayLoaderConfig: { show: false } });
      });
  };

  const handleOnSubmit = (data: PayAndStoreAchFormData) => {
    onBeforeSubmit?.();
    setData({ overlayLoaderConfig: { show: true } });
    onSubmitAchForm(data);
  };

  useEffect(() => {
    onLoadComplete();
  }, []);

  const isGuestUser = !getCustomerId(
    originalCheckoutSessionResponse,
  );

  const shouldShowSaveOptionsSection = shouldShowSaveOptions({
    isGuestUser,
    paymentMethodSaveMode,
    showSaveFeatureBlock,
  });

  return (
    <PayAndStoreAchForm
      paymentMethodSaveMode={paymentMethodSaveMode}
      shouldShowSaveOptionsSection={shouldShowSaveOptionsSection}
      formTitle={formTitle}
      bankName={bankName}
      amount={amount}
      actionType="make_payment"
      isInFocus={isInFocus}
      onCancel={onCancel}
      onSubmit={handleOnSubmit}
      onRoutingNumberInput={onRoutingNumberInput}
      onBackClick={onBackClick}
    />
  );
};

export default PayAndStoreAchContainer;
